import React, { useState, useEffect } from 'react';
import { colors } from '../../../Colors';
import { clipAudioApi } from '../../../services/api';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Movie, MusicNote, ExpandMore, ExpandLess } from '@mui/icons-material';

function Step2SceneGeneration({ projectId, projectData, onScenesGenerated, onNextStep, onRefreshProject }) {
  const [scenePrompt, setScenePrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      const result = await clipAudioApi.executeStep(projectId, projectData.current_step);
      console.log('Generation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
    } catch (error) {
      console.error('Error generating scenes:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    
    try {
      const result = await clipAudioApi.validateStep(projectId, projectData.current_step);
      console.log('Validation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
      // Handle successful validation
      if (onNextStep) {
        onNextStep();
      }
    } catch (error) {
      console.error('Error validating step:', error);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Box sx={{ p: 2.5 }}>
      {/* Compact Header with Project Name, Step, Theme and Lyrics Toggle */}
      <Box sx={{ 
        bgcolor: '#F8F9FA', 
        borderRadius: 2, 
        p: 2,
        mb: 2.5,
        border: '1px solid #E1E8ED'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
              Étape 2: Génération des Scènes
            </Typography>
            {/* Theme Display */}
            {(() => {
              const theme = projectData?.lyric?.theme || projectData?.theme;
              const themeText = typeof theme === 'string' ? theme : (theme?.theme || '');
              return themeText && (
                <Typography variant="body1" sx={{ color: '#2C3E50', fontWeight: 'bold' }}>
                  Thème: {themeText}
                </Typography>
              );
            })()}
          </Box>
          {projectData && (projectData.lyrics || projectData.lyric?.lyric) && (
            <Button
              onClick={() => setShowLyrics(!showLyrics)}
              startIcon={showLyrics ? <ExpandLess /> : <ExpandMore />}
              variant="outlined"
              size="small"
              sx={{
                borderColor: '#3498DB',
                color: '#3498DB',
                '&:hover': {
                  borderColor: '#2980B9',
                  backgroundColor: '#E8F4FD'
                }
              }}
            >
              {showLyrics ? 'Masquer' : 'Voir paroles'}
            </Button>
          )}
        </Box>

        
        {/* Lyrics Display */}
        {projectData && showLyrics && (projectData.lyrics || projectData.lyric?.lyric) && (
          <Box sx={{ 
            bgcolor: '#E8F4FD', 
            borderRadius: 1, 
            p: 1.5,
            mt: 1.5,
            border: '1px solid #3498DB'
          }}>
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: '0.85rem',
                lineHeight: 1.4,
                color: '#7F8C8D',
                margin: 0
              }}
            >
              {projectData.lyrics || projectData.lyric?.lyric}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Scene Generation Form */}
             <Card sx={{ mb: 2.5, borderRadius: 3, bgcolor: '#F8F9FA' }}>
        <CardContent sx={{ p: 3 }}>
                     <Typography variant="h4" component="h3" sx={{ 
             textAlign: 'center', 
             mb: 2, 
             fontWeight: 'bold',
             color: '#2C3E50',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             gap: 1
           }}>
            <Movie color="warning" />
            Génération des Scènes
          </Typography>
          
          <Box>
            <TextField
              fullWidth
              label="Décrivez le style visuel des scènes"
              placeholder="Ex: Des scènes urbaines avec des couleurs vives et des transitions fluides..."
              value={scenePrompt}
              onChange={(e) => setScenePrompt(e.target.value)}
              multiline
              rows={3}
              variant="outlined"
              sx={{ mb: 2.5 }}
            />


            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={20} /> : <Movie />}
                sx={{
                  background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  minWidth: '150px',
                  boxShadow: '0 8px 25px rgba(52, 152, 219, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 30px rgba(52, 152, 219, 0.6)'
                  },
                  '&:disabled': {
                    background: '#BDBDBD',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {isGenerating ? 'Génération...' : 'Générer'}
              </Button>
              
              <Button
                variant="contained"
                onClick={handleValidate}
                disabled={isValidating || projectData?.step_status !== "completed"}
                startIcon={isValidating ? <CircularProgress size={20} /> : <Movie />}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  minWidth: '150px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 30px rgba(39, 174, 96, 0.6)'
                  },
                  '&:disabled': {
                    background: '#BDBDBD',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {isValidating ? 'Validation...' : 'Valider'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Display Scenes when step is completed */}
      {projectData?.step_status === "completed" && projectData?.scenes?.scenes && (
        <Card sx={{ mt: 2.5, borderRadius: 3, bgcolor: '#FFF8E1' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" component="h4" sx={{ 
              textAlign: 'center', 
              mb: 2, 
              fontWeight: 'bold',
              color: '#2C3E50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <Movie color="warning" />
              Scènes générées ({projectData.scenes.nbScenes} scènes)
            </Typography>
            
            {/* Table for lyric_line and scene description */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                      🎵 Ligne de paroles
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                      📝 Description de la scène
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectData.scenes.scenes.map((scene, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#FAFAFA' } }}>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: '#E65100',
                        fontSize: '0.95rem',
                        verticalAlign: 'top',
                        width: '40%'
                      }}>
                        {scene.lyric_line || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#2C3E50',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        verticalAlign: 'top'
                      }}>
                        {scene.one_line}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => handleValidate()}
                startIcon={<Movie />}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '180px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)'
                  }
                }}
              >
                Continuer vers l'Étape 3
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

    </Box>
  );
}

export default Step2SceneGeneration;
